import { useEffect, useRef, useState } from "react";
import type { AiMember } from "@shared/types/supabase.types";
import type { HumanMember } from "@/features/containers/containers.types";
import { supabase } from "@/lib/supabase";
import { isMemberType } from "@/features/containers/containers.utils";

type PresenceStatus = "active" | "inactive";
type PresencePayload = {
  user_id: string;
  status: PresenceStatus;
  presence_ref: string;
  online_at?: string;
  last_active?: string;
};

export type PresenceMap = Record<string, PresencePayload>;

const CHANNEL_NAME = "online-users";
const ACTIVE_WINDOW = 5 * 60 * 1000;
const HEARTBEAT = 30_000;

// Module-level observer — usePresenceState components subscribe here instead of
// creating a second channel, which would duplicate presence entries.
const presenceListeners = new Set<(users: string[]) => void>();
let cachedOnlineUsers: string[] = [];

function broadcastPresenceState(channel: ReturnType<typeof supabase.channel>) {
  const state = channel.presenceState();
  const userIds: string[] = [];
  for (const id in state) {
    const entries = state[id] as unknown as PresencePayload[];
    if (!entries.length) continue;
    // Each track() call appends a new entry — use the last one (most recent)
    const latest = entries[entries.length - 1];
    if (latest.status === "active") {
      userIds.push(latest.user_id);
    }
  }
  cachedOnlineUsers = [...new Set(userIds)];
  presenceListeners.forEach((fn) => fn(cachedOnlineUsers));
}

export function usePresence(userId: string) {
  const lastActivity = useRef(Date.now());
  const lastPersistedAt = useRef(0);
  const lastPersistedActivity = useRef(0);
  const lastPersistedStatus = useRef<PresenceStatus | null>(null);

  useEffect(() => {
    if (!userId) return;

    const channel = supabase.channel(CHANNEL_NAME);

    const getStatus = () =>
      Date.now() - lastActivity.current < ACTIVE_WINDOW ? "active" : "inactive";

    const maybePersistActivity = (status: PresenceStatus) => {
      if (!userId) return;

      const lastActiveMs = lastActivity.current;
      const now = Date.now();

      const lastPersistedStatusValue = lastPersistedStatus.current;
      const shouldPersist =
        status !== lastPersistedStatusValue ||
        lastActiveMs - lastPersistedActivity.current >= 60_000 ||
        now - lastPersistedAt.current >= 300_000;

      if (!shouldPersist) {
        return;
      }

      lastPersistedStatus.current = status;
      lastPersistedAt.current = now;
      lastPersistedActivity.current = lastActiveMs;

      const payload = {
        p_kind: "presence" as const,
        p_last_active_at: new Date(lastActiveMs).toISOString(),
        p_status: status,
      };

      void (async () => {
        const { error } = await supabase.rpc("record_user_activity", payload);
        if (error) {
          console.error("Failed to persist user presence activity", error);
        }
      })();
    };

    const updateActivity = () => {
      lastActivity.current = Date.now();
      maybePersistActivity("active");
    };

    window.addEventListener("mousemove", updateActivity);
    window.addEventListener("keydown", updateActivity);
    window.addEventListener("click", updateActivity);
    window.addEventListener("scroll", updateActivity);

    channel
      .on("presence", { event: "sync" }, () => broadcastPresenceState(channel))
      .on("presence", { event: "join" }, () => broadcastPresenceState(channel))
      .on("presence", { event: "leave" }, () => broadcastPresenceState(channel))
      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          const presenceStatus = getStatus();
          const payload = {
            online_at: new Date().toISOString(),
            user_id: userId,
            status: presenceStatus,
            last_active: new Date(lastActivity.current).toISOString(),
          };
          void channel.track(payload);
          maybePersistActivity(presenceStatus);
        }
      });

    const interval = setInterval(() => {
      const presenceStatus = getStatus();
      const payload = {
        online_at: new Date().toISOString(),
        user_id: userId,
        status: presenceStatus,
        last_active: new Date(lastActivity.current).toISOString(),
      };
      void channel.track(payload);
      maybePersistActivity(presenceStatus);
    }, HEARTBEAT);

    return () => {
      clearInterval(interval);

      window.removeEventListener("mousemove", updateActivity);
      window.removeEventListener("keydown", updateActivity);
      window.removeEventListener("click", updateActivity);
      window.removeEventListener("scroll", updateActivity);

      void channel.unsubscribe();
    };
  }, [userId]);
}

export function usePresenceState() {
  const [onlineUsers, setOnlineUsers] = useState<string[]>(cachedOnlineUsers);

  useEffect(() => {
    presenceListeners.add(setOnlineUsers);
    return () => {
      presenceListeners.delete(setOnlineUsers);
    };
  }, []);

  return onlineUsers;
}

export function getPresenceStatus(
  member: HumanMember | AiMember,
  onlineUsers: string[],
): PresenceStatus {
  if (isMemberType("ai")(member)) return "active";
  if (isMemberType("invited_user")(member)) return "inactive";
  return onlineUsers.includes(member.user_id) ? "active" : "inactive";
}
